import AutoGraphIcon from "@mui/icons-material/AutoGraph";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import {
  Pagination,
  Typography,
} from "@mui/material";
import { uniq } from "lodash";
import { useEffect, useState } from "react";
import { flushSync } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import CustomButton from "../../../../components/CustomButton";
import CustomCheckbox from "../../../../components/CustomCheckbox";
import CustomModal from "../../../../components/CustomModal/CustomModal";
import CustomTextField from "../../../../components/CustomTextField";
import CustomSelect from "../../../../components/Select/Select";
import { getAppointmentsFacilityReportData } from "../../../../redux/reducers/reportSlice";
import { AppColors } from "../../../../util/AppColors";
import { w20h20BlueIcon } from "../../../../util/CommonIconStyle";
import { facilityTypes } from "../../../../util/dropList";
import ReportTable from "../ReportTable";
import { nameExpan } from "../tableHeaders";
import "./FacilityUtilization.css";

const tableHdrs = [
  "name",
  "facilityType",
  "appointments",
  "avgDownTime",
  "utilization",
];
const initSearch = {
  facilityName: "",
  facilityType: [],
};

const AppointmentsFacilityReport = () => {
  const dispatch = useDispatch();
  const [selectedPeriod, setSelectedPeriod] = useState("day");
  const [crntTblHeders, setCrntTblHeders] = useState(tableHdrs);
  const [activeTableHeaders, setActiveTableHeaders] = useState(
    tableHdrs?.map((pd, i) => ({ name: pd, isSelected: true, i })),
  );
  const [editColModVsble, setEditColModVsble] = useState(false);
  const [page, setPage] = useState(1);
  const [searchValues, setSearchValues] = useState(initSearch);
  const { appoinmentFacility } = useSelector((state) => state?.report || {});
  const rowsPerPage = 10;

  useEffect(() => {
    onApiCall(1);
  }, [selectedPeriod, searchValues?.facilityName, searchValues?.facilityType]);

  const facilityPayload = appoinmentFacility?.utilizationData || appoinmentFacility?.data || appoinmentFacility || {};
  const cards = facilityPayload?.cards || appoinmentFacility?.cards || {};

  const summaryCards = [
    {
      title: "Facilities tracked",
      value: cards?.facilitiesTracked !== undefined ? `${cards.facilitiesTracked}` : "0",
    },
    {
      title: "Avg utilization",
      value: cards?.avgUtilizationDisplay ?? (cards?.avgUtilization !== undefined ? `${cards.avgUtilization}%` : "0%"),
    },
    {
      title: "Busiest facility",
      value: cards?.busiestFacility?.facilityName || cards?.busiestFacility?.name || "—",
    },
  ];

  const getFilterObj = (currentPage = 1, reset = false) => {
    setPage(currentPage);
    const reqObj = {
      type: "facility",
      page: currentPage,
      itemSize: rowsPerPage,
      period: selectedPeriod,
      startDate: "2025-08-01",
      endDate: "2026-08-31",
    };
    if (reset) return reqObj;
    if (searchValues?.facilityName?.length > 0) {
      reqObj.facilityName = [searchValues?.facilityName];
    }
    if (searchValues?.facilityType?.length > 0) {
      reqObj.facilityType = searchValues?.facilityType?.map((li) => li?.value);
    }
    return reqObj;
  };

  const onApiCall = (pge = 1, reset = false) => {
    dispatch(getAppointmentsFacilityReportData(getFilterObj(pge, reset)));
  };

  const handleEditColumn = () => {
    const reqList = tableHdrs?.map((pd, i) => {
      const isObjMat = activeTableHeaders?.find((tblHdr) => tblHdr?.name === pd);
      return { name: pd, isSelected: isObjMat?.isSelected ?? false, i };
    });
    setActiveTableHeaders(reqList);
    setEditColModVsble(true);
  };

  const onEditModClose = () => setEditColModVsble(false);

  const handleSelcetdColumn = (selected, ind) => {
    const reqList = activeTableHeaders?.map((act, i) => {
      if (i === ind) return { ...act, isSelected: selected };
      return act;
    });
    setActiveTableHeaders(reqList);
  };

  const handleSaveTableColumn = () => {
    const reqList = activeTableHeaders?.filter((act) => act?.isSelected)?.map((act) => act?.name);
    setCrntTblHeders(reqList);
    setEditColModVsble(false);
  };

  const rawFacilitiesList = Array.isArray(facilityPayload?.facilities)
    ? facilityPayload.facilities
    : Array.isArray(appoinmentFacility?.data)
    ? appoinmentFacility.data
    : [];

  const totalPage = Math.ceil((appoinmentFacility?.totalRecords ?? rawFacilitiesList.length) / rowsPerPage);

  const handleChangePage = (e, selectedPage) => onApiCall(selectedPage);

  const getTableData = () => {
    return rawFacilitiesList.map((li) => ({
      ...li,
      name: li?.facilityName || li?.name || "—",
      facilityType: li?.facilityType || "General",
      appointments: li?.appointmentCount ?? li?.appointments ?? 0,
      avgDownTime:
        li?.bookedMinutes !== undefined && li?.availableMinutes
          ? `${Math.round(((li.availableMinutes - li.bookedMinutes) / 60) * 10) / 10} hrs`
          : li?.avgDownTime ?? "0 hrs",
      utilization: li?.utilizationDisplay || (li?.utilization !== undefined ? `${li.utilization}%` : "0.00%"),
    }));
  };

  const onSearchChange = (e) => {
    const { name, value } = e?.target;
    setSearchValues({ ...searchValues, [name?.trim()?.replace(/,$/, "")]: value });
  };

  const onReset = () => {
    flushSync(() => setSearchValues(initSearch));
    onApiCall(1, true);
  };

  const getFacilityList = () => uniq(rawFacilitiesList.map((li) => li?.facilityName || li?.name)?.filter(Boolean));

  return (
    <div className="facility-utilization-container">
      {/* Title */}
      <h2 className="facility-utilization-title">Facility utilization</h2>

      {/* Top Filter Bar */}
      <div className="facility-utilization-filter-bar">
        <div className="facility-utilization-period-group">
          {[
            { label: "Day", value: "day" },
            { label: "Week", value: "week" },
            { label: "Month", value: "month" },
          ].map((item) => (
            <button
              key={item.value}
              type="button"
              className={`facility-utilization-period-btn ${selectedPeriod === item.value ? "active" : ""
                }`}
              onClick={() => setSelectedPeriod(item.value)}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>

      {/* Summary Cards Grid (3 Cards) */}
      <div className="facility-utilization-cards-grid">
        {summaryCards.map((card, idx) => (
          <div className="facility-utilization-card" key={idx}>
            <h4 className="facility-utilization-card-title">{card.title}</h4>
            <div className="facility-utilization-card-value-container">
              <h2 className="facility-utilization-card-value">{card.value}</h2>
            </div>
          </div>
        ))}
      </div>

      {/* Drill-down Header */}
      <div className="facility-utilization-drilldown-header">Drill-down — per facility</div>

      {/* Search Filters Row */}
      <div className="flex-row mv10">
        <div className="top-row-cen-con w100Per">
          <div className="w15Per">
            <CustomTextField
              options={getFacilityList()}
              value={searchValues?.facilityName ?? []}
              name="facilityName"
              handleChange={onSearchChange}
              label="Facility Name"
              backgroundColor={AppColors.lightBlue2}
              suggetions
              fullWidth
            />
          </div>
          <div className="w15Per ml20">
            <CustomSelect
              list={facilityTypes}
              value={searchValues?.facilityType ?? []}
              name="facilityType"
              handleChange={onSearchChange}
              label="Facility Type"
              backgroundColor={AppColors.lightBlue2}
              multiSelectSearchCheck
            />
          </div>
          <div className="ml20">
            <CustomButton text="Reset" onClick={onReset} smallBtn redBtn />
          </div>
          <div
            className="flex-row-ali-cen ml20 cursor"
            onClick={handleEditColumn}
          >
            <EditOutlinedIcon sx={w20h20BlueIcon} />
            <Typography className="blue-color fs16 font-medium ml5">
              Edit columns ({crntTblHeders?.length})
            </Typography>
          </div>
        </div>
      </div>

      <Typography className="font-medium fs12 gray28 ml30">
        Number of Records: {appoinmentFacility?.totalRecords ?? rawFacilitiesList.length}
      </Typography>

      <div className="mtMin10">
        <ReportTable
          datas={getTableData()}
          columns={crntTblHeders}
          maxHeight="none"
        />
        {Math.ceil((appoinmentFacility?.totalRecords ?? rawFacilitiesList.length) / rowsPerPage) > 1 ? (
          <div className="flex1-end mt20 mb30">
            <Pagination
              count={totalPage}
              variant="outlined"
              color="primary"
              page={page}
              onChange={handleChangePage}
            />
          </div>
        ) : null}
      </div>

      {/* Edit Columns Modal */}
      <CustomModal open={editColModVsble} onClose={onEditModClose} header="Columns" modal modalWidth={25}>
        <div className="ph20 scroll-70vh">
          {activeTableHeaders?.map((hr) => (
            <div className="flex-row" key={hr?.i}>
              <CustomCheckbox
                label={nameExpan?.[hr?.name]}
                checked={hr?.isSelected}
                onChange={() => handleSelcetdColumn(!hr?.isSelected, hr?.i)}
              />
            </div>
          ))}
          <div className="flex-end mb10">
            <CustomButton text="Save" smallBtn onClick={handleSaveTableColumn} />
          </div>
        </div>
      </CustomModal>
    </div>
  );
};

export default AppointmentsFacilityReport;
